import { interpolateString } from "@memorang/helpers/common";
import { Text } from "react-native-paper";
import { useDevice } from "../../../../useDevice";
import AudioPlayerContainer from "../../../AudioPlayerContainer";
import Box from "../../../Box";
import ImagesCarousel, { type ImageType } from "../../../ImagesCarousel";
import InfoAlert from "../../../InfoAlert";
import MarkdownRenderer from "../../../MarkdownRenderer";
import type { ItemAudioGate } from "../../types";

type Props = {
	passage?: string;
	instruction?: string;
	currentItemIndex?: number;
	numTotalItems?: number;
	audioUri?: string;
	images?: ImageType[];
	passageTitle?: string;
	audioGate?: ItemAudioGate;
	// If true, renders media edge-to-edge with no gaps.
	fullBleed?: boolean;
};

const PassageContent = ({
	passage,
	instruction,
	currentItemIndex,
	numTotalItems,
	audioUri,
	images: stimulusImages = [],
	passageTitle,
	audioGate,
	fullBleed,
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: na
}: Props) => {
	const { isMobile } = useDevice();
	const images = [...stimulusImages];

	const resolvedInstruction =
		currentItemIndex === 1 && instruction && numTotalItems != null
			? interpolateString(instruction, { n: numTotalItems })
			: undefined;

	const infoAlert = resolvedInstruction ? (
		<InfoAlert
			text={resolvedInstruction}
			{...(fullBleed
				? {
						marginHorizontal: -16,
						borderRadius: 0,
						paddingHorizontal: 16,
						paddingVertical: 8,
					}
				: {})}
		/>
	) : null;

	const audioPlayerElement = audioUri ? (
		<AudioPlayerContainer
			ref={audioGate?.playerRef}
			uri={audioUri}
			waveformWidth={isMobile ? 200 : 320}
			showWaveform
			useReanimated={false}
			playOnce={audioGate?.playOnce}
			forceCompleted={audioGate?.forceCompleted}
			onEnd={audioGate?.onEnd}
			borderRadius={fullBleed ? 0 : undefined}
		/>
	) : null;

	const audioPlayer =
		audioPlayerElement && fullBleed ? (
			<Box marginHorizontal={-16}>{audioPlayerElement}</Box>
		) : (
			audioPlayerElement
		);

	const imagesCarousel =
		images.length > 0 ? (
			<ImagesCarousel
				images={images}
				disableSingleImageSizeLimits
				imageBorderRadius={fullBleed ? 0 : undefined}
			/>
		) : null;

	return (
		<Box gap={fullBleed ? 0 : 12}>
			{isMobile && audioPlayer ? (
				<>
					{audioPlayer}
					{infoAlert}
				</>
			) : (
				<>
					{infoAlert}
					{audioPlayer}
				</>
			)}

			{passage || passageTitle ? (
				<Box gap={12}>
					{passageTitle ? (
						<Text variant="titleLarge">{passageTitle}</Text>
					) : null}
					<MarkdownRenderer
						text={passage ?? ""}
						variant="explanation"
						fontSize={16}
						paragraphMarginVertical={8}
					/>
				</Box>
			) : null}
			{fullBleed && imagesCarousel ? (
				<Box marginHorizontal={-16}>{imagesCarousel}</Box>
			) : (
				imagesCarousel
			)}
		</Box>
	);
};

export default PassageContent;
